import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  signupUser,
  signupWithGoogle,
  isSuccessResponse, 
  getResponseData, 
  getResponseMessage, 
  getErrorMessage 
} from '../components/apiClient.js';
import { useJWTAuth } from '../components/JWTAuthContext.js';
import { SuccessModal, ErrorModal } from '../components/common';
import GoogleSignupButton from '../components/GoogleSignupButton';

interface ModalData {
  title: string;
  message: string;
  buttonText: string;
  secondaryButton?: string;
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { login, setAuthData } = useJWTAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showGoogleLoginModal, setShowGoogleLoginModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({
    title: '',
    message: '',
    buttonText: ''
  });
  
  // Google user data for potential login redirect
  const [googleUserData, setGoogleUserData] = useState<any>(null);

  // Check for pending Google login data on component mount
  useEffect(() => {
    const pendingGoogleData = sessionStorage.getItem('pendingGoogleLogin');
    if (pendingGoogleData) {
      try {
        const userData = JSON.parse(pendingGoogleData);
        setGoogleUserData(userData);
        // Clear the stored data
        sessionStorage.removeItem('pendingGoogleLogin');
        
        // Show helpful message about continuing with Google
        setModalData({
          title: 'Continue with Google',
          message: `Welcome! You can create your account with your Google profile (${userData.email}) or try a different signup method.`,
          buttonText: 'Got it'
        });
        setShowSuccessModal(true);
      } catch (error) {
        console.error('Error parsing pending Google data:', error);
        sessionStorage.removeItem('pendingGoogleLogin');
      }
    }
  }, []);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Hidden fields with default values
    firstName: '',
    lastName: '',
    phone: '',
    examPreference: '',
    grade: '',
    agreeToTerms: true,
    subscribeNewsletter: true
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setModalData({
        title: 'Password Mismatch',
        message: 'The passwords you entered do not match. Please ensure both password fields are identical.',
        buttonText: 'Fix Password'
      });
      setShowErrorModal(true);
      return;
    }
    
    if (!formData.agreeToTerms) {
      setModalData({
        title: 'Terms and Conditions Required',
        message: 'Please read and agree to our terms and conditions to continue with your account creation.',
        buttonText: 'I Understand'
      });
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);

    try {
      // Split full name into first and last name
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Prepare signup data
      const signupData = {
        firstName: firstName,
        lastName: lastName,
        email: formData.email,
        phone: '', // Default empty
        password: formData.password,
        examPreference: '', // Default empty
        grade: '' // Default empty
      };

      // Call signup API using the new API client
      const response = await signupUser(signupData);

      if (isSuccessResponse(response)) {
        // After successful signup, automatically log the user in with JWT
        const loginResult = await login({
          username: formData.email,
          password: formData.password
        });
        
        if (loginResult.success) {
          // Show success message with modal
          setModalData({
            title: 'Welcome to WiZiAi! 🎉',
            message: 'Your account has been created successfully and you are now logged in. Get ready to start your learning journey!',
            buttonText: 'Go to Dashboard'
          });
          setShowSuccessModal(true);
        } else {
          // Signup succeeded but auto-login failed - show success modal with login redirect
          setModalData({
            title: 'Account Created Successfully! ✅',
            message: 'Your account has been created successfully. Please log in with your credentials to access your dashboard.',
            buttonText: 'Go to Login'
          });
          setShowSuccessModal(true);
        }
      } else {
        const errorMessage = getResponseMessage(response) || 'Signup failed. Please try again.';
        setModalData({
          title: 'Signup Failed',
          message: errorMessage,
          buttonText: 'Try Again'
        });
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = getErrorMessage(error) || 'An unexpected error occurred. Please try again.';
      setModalData({
        title: 'Connection Error',
        message: errorMessage,
        buttonText: 'Retry'
      });
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Modal handlers
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Navigate based on the modal content
    if (modalData.buttonText === 'Go to Dashboard') {
      navigate('/dashboard', { replace: true });
    } else if (modalData.buttonText === 'Go to Login') {
      navigate('/login', { replace: true });
    }
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
  };

  // Google signup handler
  const handleGoogleSignup = async (googleUserData: any) => {
    if (isLoading) return;

    // Check terms agreement first
    if (!formData.agreeToTerms) {
      setModalData({
        title: 'Terms and Conditions Required',
        message: 'Please read and agree to our terms and conditions to continue with your account creation.',
        buttonText: 'I Understand'
      });
      setShowErrorModal(true);
      return;
    }

    // Store Google user data for potential login redirect
    setGoogleUserData(googleUserData);

    setIsLoading(true);
    try {
      // Call Google signup API
      const response = await signupWithGoogle(googleUserData);

      if (isSuccessResponse(response)) {
        const userData = getResponseData(response);
        
        // The Google signup API should return JWT tokens just like login
        if (userData && userData.access_token) {
          // Use auth context to set user data and tokens
          const authResult = setAuthData(
            userData.user, 
            userData.access_token, 
            userData.refresh_token,
            userData.expires_in,
            userData.refresh_expires_in
          );
          
          if (authResult.success) {
            // Show welcome modal with Google signup success
            setModalData({
              title: 'Welcome to WiZiAi! 🎉',
              message: `Hi ${googleUserData.firstName}! Your Google account has been linked successfully. You've received ${userData?.signup_bonus?.points_awarded || 120} bonus points! Please complete your profile to get the best experience.`,
              buttonText: 'Go to Dashboard'
            });
            setShowSuccessModal(true);
          } else {
            throw new Error('Failed to set authentication data');
          }
        } else {
          // Fallback to manual login if tokens not provided
          console.warn('No JWT tokens in signup response, attempting manual login...');
          const loginResult = await login({
            username: googleUserData.email,
            password: '' // Google OAuth doesn't need password
          });
          
          if (loginResult.success) {
            setModalData({
              title: 'Welcome to WiZiAi! 🎉',
              message: `Hi ${googleUserData.firstName}! Your Google account has been linked successfully. You've received ${userData?.signup_bonus?.points_awarded || 120} bonus points!`,
              buttonText: 'Go to Dashboard'
            });
            setShowSuccessModal(true);
          } else {
            // Signup succeeded but auto-login failed
            setModalData({
              title: 'Account Created Successfully! ✅',
              message: 'Your Google account has been linked to WiZiAi. Please log in to access your dashboard.',
              buttonText: 'Go to Login'
            });
            setShowSuccessModal(true);
          }
        }
      } else {
        const errorMessage = getResponseMessage(response) || 'Google signup failed. Please try again.';
        
        // Check if the error indicates account already exists
        const isAccountExists = errorMessage.toLowerCase().includes('account with this email already exists') ||
                               errorMessage.toLowerCase().includes('email already exists') ||
                               errorMessage.toLowerCase().includes('user already exists');
        
        if (isAccountExists) {
          // Guide user to login instead
          setModalData({
            title: 'Account Already Exists',
            message: `An account with ${googleUserData?.email} already exists. Would you like to sign in to your existing account?`,
            buttonText: 'Sign In',
            secondaryButton: 'Try Different Email'
          });
          setShowGoogleLoginModal(true);
        } else {
          // Generic error handling
          setModalData({
            title: 'Google Signup Failed',
            message: errorMessage,
            buttonText: 'Try Again'
          });
          setShowErrorModal(true);
        }
      }
    } catch (error) {
      console.error('Google signup error:', error);
      const errorMessage = getErrorMessage(error) || 'An error occurred during Google signup. Please try again.';
      setModalData({
        title: 'Connection Error',
        message: errorMessage,
        buttonText: 'Retry'
      });
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Google signup error handler
  const handleGoogleSignupError = (error: Error) => {
    console.error('Google OAuth error:', error);
    setModalData({
      title: 'Google Authentication Failed',
      message: error.message || 'Failed to authenticate with Google. Please try again or use email signup.',
      buttonText: 'Try Again'
    });
    setShowErrorModal(true);
  };

  return (
    <div className="min-vh-100 d-flex align-items-center py-5" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 col-xl-6">
            <div className="card border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="card-body p-5">
                {/* Logo and Header */}
                <div className="text-center mb-4">
                  {/* <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)'
                    }}>
                    <img src="/logo.png" alt="wiziai Logo" style={{ border: '2px solid white', borderRadius: '10%', width: '40px', height: '40px' }} />
                  </div> */}
                  <h2 className="fw-bold mb-2" style={{ 
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Start Your Success Journey
                  </h2>
                  <p className="text-muted mb-0">Join thousands of students achieving their dreams</p>
                </div>

                {/* Social Signup */}
                <div className="d-flex justify-content-center text-center mb-4">
                  <GoogleSignupButton
                    onSuccess={handleGoogleSignup}
                    onError={handleGoogleSignupError}
                    disabled={isLoading}
                    isLoading={isLoading}
                  />
                </div>

                {/* Divider */}
                <div className="text-center mb-4">
                  <div className="d-flex align-items-center">
                    <hr className="flex-grow-1" />
                    <span className="mx-3 text-muted">or continue with email</span>
                    <hr className="flex-grow-1" />
                  </div>
                </div>

                {/* Signup Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="fullName" className="form-label fw-medium">
                      <i className="fas fa-user me-2 text-primary"></i>
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                      disabled={isLoading}
                      style={{ 
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-medium">
                      <i className="fas fa-envelope me-2 text-primary"></i>
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-lg"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                      style={{ 
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="password" className="form-label fw-medium">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create password (minimum 8 characters)"
                      required
                      minLength={8}
                      disabled={isLoading}
                      style={{ 
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label fw-medium">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-lg"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      required
                      disabled={isLoading}
                      style={{ 
                        borderRadius: '12px',
                        border: '2px solid #e9ecef',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  <div className="mb-4">
                    <div className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="agreeToTerms"
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleInputChange}
                        required
                      />
                      <label className="form-check-label text-muted" htmlFor="agreeToTerms">
                        I agree to the 
                        <a 
                          href="/terms" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-decoration-none ms-1" 
                          style={{ color: '#667eea' }}
                        >
                          Terms and Conditions
                        </a> and 
                        <a 
                          href="/privacy-policy" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-decoration-none ms-1" 
                          style={{ color: '#667eea' }}
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="subscribeNewsletter"
                        name="subscribeNewsletter"
                        checked={formData.subscribeNewsletter}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label text-muted" htmlFor="subscribeNewsletter">
                        Subscribe to study tips and updates
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-lg w-100 mb-4 fw-bold"
                    disabled={isLoading}
                    style={{
                      background: isLoading ? '#6c757d' : 'linear-gradient(135deg, #667eea, #764ba2)',
                      border: 'none',
                      borderRadius: '12px',
                      color: 'white',
                      transition: 'all 0.3s ease',
                      opacity: isLoading ? 0.7 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}>
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-rocket me-2"></i>
                        Create My WiZiAi Account
                      </>
                    )}
                  </button>
                </form>

                {/* Login Link */}
                <div className="text-center">
                  <p className="text-muted mb-0">
                    Already have an account? 
                    <Link 
                      to="/login" 
                      className="text-decoration-none fw-bold ms-1"
                      style={{ color: '#667eea' }}>
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            {/* <div className="text-center mt-4">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-white opacity-75">
                    <i className="fas fa-gift fa-lg mb-2 d-block"></i>
                    <small>Free 7-Day Trial</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-white opacity-75">
                    <i className="fas fa-shield-alt fa-lg mb-2 d-block"></i>
                    <small>100% Secure</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-white opacity-75">
                    <i className="fas fa-headset fa-lg mb-2 d-block"></i>
                    <small>24/7 Support</small>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        title={modalData.title}
        message={modalData.message}
        buttonText={modalData.buttonText}
        onClose={handleSuccessModalClose}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        title={modalData.title}
        message={modalData.message}
        buttonText={modalData.buttonText}
        onClose={handleErrorModalClose}
      />

      {/* Google Login Guidance Modal */}
      {showGoogleLoginModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '15px' }}>
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">{modalData.title}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowGoogleLoginModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">{modalData.message}</p>
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => setShowGoogleLoginModal(false)}
                >
                  {modalData.secondaryButton || 'Cancel'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowGoogleLoginModal(false);
                    // Store Google data for login page
                    if (googleUserData) {
                      sessionStorage.setItem('pendingGoogleLogin', JSON.stringify(googleUserData));
                    }
                    navigate('/login');
                  }}
                >
                  {modalData.buttonText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
